import React, { ReactNode, useMemo } from 'react';

import { useAppSelector } from 'hooks/hooks';
import { colors, getFormattedDate } from 'utilities';

import styles from './styles.module.scss';
import leafIcon from '../../../assets/icons/maple-leaf.svg';
import { isVac } from 'utilities/is-vac';
import { Helmet } from 'react-helmet-async';
import { Language } from 'providers/translates/enums';
import { useTranslates } from 'providers/translates';

const tryURL = (value: string): ReactNode | string => {
  if (!value) return '';
  try {
    const string = value.trim();
    if (!/\s/.test(string) && /\./.test(string) && !/^https?:\/\//i.test(string)) {
      value = 'http://' + string;
    }
    new URL(value);
    return (
      <a href={value} target="_blank" rel="noreferrer" style={{ color: colors.blue }}>
        {value}
      </a>
    );
  } catch (error) {
    return value;
  }
};

const translationsHelmet = {
  titleEN: 'Saluting Service - Quilt square',
  titleFR: 'Saluons leur service - Carreau de courtepointe'
} as const;

export const HeaderInfo: React.FC = () => {
  const editorData = useAppSelector((state) => state.quilt.editableQuilt);

  const { translates: t } = useTranslates();
  const getValuesColumn1 = useMemo(() => {
    return [
      editorData?.dateOfBirth
        ? { title: 'Date of birth', value: getFormattedDate(editorData.dateOfBirth) }
        : null,
      editorData?.placesOfResidence &&
      editorData?.placesOfResidence?.length > 0 &&
      editorData?.placesOfResidence?.[0]
        ? { title: 'Place of Residence', value: editorData.placesOfResidence }
        : null,
      editorData?.hometown ? { title: 'Hometown', value: editorData.hometown } : null,
      editorData?.educations && editorData?.educations?.length > 0 && editorData?.educations?.[0]
        ? { title: 'Education', value: editorData.educations }
        : null,
      editorData?.hobbiesAndPasttimes &&
      editorData?.hobbiesAndPasttimes?.length > 0 &&
      editorData?.hobbiesAndPasttimes?.[0]
        ? { title: 'Hobbies and Pastimes', value: editorData.hobbiesAndPasttimes }
        : null,
    ];
  }, [editorData]);

  const getValuesColumn2 = useMemo(() => {
    return [
      editorData?.communityInvolvements &&
      editorData?.communityInvolvements?.length > 0 &&
      editorData?.communityInvolvements?.[0]
        ? { title: 'Community Involvement', value: editorData.communityInvolvements }
        : null,
      editorData?.dateOfPassing
        ? { title: 'Date of Death', value: getFormattedDate(editorData.dateOfPassing) }
        : null,
      editorData?.placeOfBurial
        ? { title: 'Place of Burial', value: editorData.placeOfBurial }
        : null,
      editorData?.inMemorialDonation
        ? { title: 'Donation in memory', value: editorData.inMemorialDonation }
        : null,
      editorData?.donationLink
        ? { title: 'Donation Link', value: tryURL(editorData.donationLink) }
        : null,
    ];
  }, [editorData]);

  const getItemValue = (value: string | string[] | ReactNode): string | ReactNode => {
    if (!value) return '';

    if (Array.isArray(value)) {
      return (
        <div className={styles.arrayType}>
          {value.map((item, index) => {
            return <span key={index}>{item}</span>;
          })}
        </div>
      );
    } else return value;
  };

  const getDateUnderPhoto = useMemo(() => {
    if (!editorData?.dateOfBirth || !editorData?.dateOfPassing) return;

    return `${editorData?.dateOfBirth.year} - ${editorData?.dateOfPassing.year}`;
  }, [editorData?.dateOfBirth, editorData?.dateOfPassing]);

  return (
    editorData && (
      <>
      <div className={styles.content}>
        <div className={styles.contentLeft}>
          <div className={styles.contentLeftInside}>
            {editorData.previewImageUrl ? (
              <img
                className={styles.details__addPhoto}
                src={editorData.previewImageUrl}
                alt={'preview'}
              />
            ) : editorData.finalImage?.full ? (
              <img
                className={styles.details__addPhoto}
                src={editorData.finalImage?.full}
                alt={editorData.finalImage?.name}
              />
            ) : (
              <div className={styles.details__noImageText}>
                <span>{t?.personPhotoUploader?.noImageLabel ?? 'No Image'}</span>
              </div>
            )}
            <span
              className={styles.title}
            >{`${editorData.firstName || ''} ${editorData?.lastName || ''}`}</span>
            <span className={styles.text}>{getDateUnderPhoto}</span>
            {(isVac() && editorData?.isDeceased) && (
              <div className={styles.leafIcon}>
                <img 
                  src={leafIcon} 
                  alt='Leaf Icon' 
                  style={{width: '24px'}}
                />
                <small className={styles.leafIcontext}>{t?.home?.deceasedText ?? 'Denotes Veteran is deceased'}</small>
              </div>
            )}
          </div>          
        </div>

        <div className={styles.contentRight}>
          <div className={styles.contentRightColumn1}>
            {getValuesColumn1.map((item, index) => {
              return item ? (
                <div className={styles.contentItem} key={index}>
                  <span className={styles.title}>{item?.title}</span>
                  <span className={styles.text}>{getItemValue(item?.value)}</span>
                </div>
              ) : null;
            })}
          </div>

          <div className={styles.contentRightColumn2}>
            {getValuesColumn2.map((item, index) => {
              return item ? (
                <div className={styles.contentItem} key={index}>
                  <span className={styles.title}>{item?.title}</span>
                  <span className={styles.text}>{getItemValue(item?.value)}</span>
                </div>
              ) : null;
            })}
          </div>
        </div>
      </div>
      </>
    )    
  );
};

export const HeaderInfoVac: React.FC = () => {
  const editorData = useAppSelector((state) => state.quilt.editableQuilt);
  
  const isFrench = editorData.lang === Language.FRENCH;
  const { translates: t, selectedLang } = useTranslates();
  const dateLanguage = (editorData.lang ?? 'en').toLowerCase() as "en" | "fr";

  const getValuesColumn1 = useMemo(() => {
    return [
      editorData?.dateOfBirth
        ? { title: isFrench ? 'Date de naissance' : 'Date of birth', value: getFormattedDate(editorData.dateOfBirth, dateLanguage) }
        : null,
      editorData?.placesOfResidence &&
      editorData?.placesOfResidence?.length > 0 &&
      editorData?.placesOfResidence?.[0]
        ? { title: isFrench ? 'LIEU DE RÉSIDENCE' : 'Place of Residence', value: editorData.placesOfResidence }
        : null,
      editorData?.hometown ? { title: 'Hometown', value: editorData.hometown } : null,
      editorData?.educations && editorData?.educations?.length > 0 && editorData?.educations?.[0]
        ? { title: isFrench ? 'ÉTUDESE' : 'Education', value: editorData.educations }
        : null,
      editorData?.hobbiesAndPasttimes &&
      editorData?.hobbiesAndPasttimes?.length > 0 &&
      editorData?.hobbiesAndPasttimes?.[0]
        ? { title: isFrench ? 'LOISIRS ET PASSE-TEMPS' : 'Hobbies and Pastimes', value: editorData.hobbiesAndPasttimes }
        : null,
    ];
  }, [editorData, editorData.lang]);

  const getValuesColumn2 = useMemo(() => {
    return [
      editorData?.communityInvolvements &&
      editorData?.communityInvolvements?.length > 0 &&
      editorData?.communityInvolvements?.[0]
        ? { title: isFrench ? 'PARTICIPATION À LA VIE COMMUNAUTAIRE' : 'Community Involvement', value: editorData.communityInvolvements }
        : null,
      editorData?.dateOfPassing
        ? { title: isFrench ? 'Date du décès' : 'Date of Death', value: getFormattedDate(editorData.dateOfPassing, dateLanguage) }
        : null,
      editorData?.placeOfBurial
        ? { title: isFrench ? 'LIEU DE SÉPULTURE' : 'Place of Burial', value: editorData.placeOfBurial }
        : null,
      editorData?.inMemorialDonation
        ? { title: isFrench ? 'DON À SA MÉMOIRE' : 'Donation in memory', value: editorData.inMemorialDonation }
        : null,
      editorData?.donationLink
        ? { title: isFrench ? 'LIEN POUR DONNER' : 'Donation Link', value: tryURL(editorData.donationLink) }
        : null,
    ];
  }, [editorData, editorData.lang]);

  const getValuesColumn3 = useMemo(() => {
    return [
      editorData?.forces &&
      editorData?.forces?.length > 0 &&
      editorData?.forces?.[0]
        ? { title: isFrench ? 'FORCE' : 'Force', value: editorData.forces }
        : null,
      editorData?.serviceNumber
        ? { title: isFrench ? 'NUMÉRO DE SERVICE' : 'Service Number', value: editorData.serviceNumber }
        : null,
      editorData?.regimentalNumber
        ? { title: isFrench ? 'NUMÉRO DE RÉGIMENT' : 'Regimental Number', value: editorData.regimentalNumber }
        : null,
      editorData?.units &&
      editorData?.units?.length > 0 &&
      editorData?.units?.[0]
        ? { title: isFrench ? 'UNITÉ' : 'Unit', value: editorData.units }
        : null,
      editorData?.troops &&
      editorData?.troops?.length > 0 &&
      editorData?.troops?.[0]
        ? { title: isFrench ? 'TROUPE' : 'Troop', value: editorData.troops }
        : null,
      editorData?.enlistmentDate
        ? { title: isFrench ? 'DATE D’ENRÔLEMENT' : 'Enlistment Date', value: getFormattedDate(editorData.enlistmentDate, dateLanguage) }
        : null,
    ];
  }, [editorData, editorData.lang]);

  const getValuesColumn4 = useMemo(() => {
    return [
      editorData?.divisions &&
      editorData?.divisions?.length > 0 &&
      editorData?.divisions?.[0]
        ? { title: isFrench ? 'DIVISION' : 'Division', value: editorData.divisions }
        : null,
      editorData?.regiments &&
      editorData?.regiments?.length > 0 &&
      editorData?.regiments?.[0]
        ? { title: isFrench ? 'RÉGIMENT' : 'Regiment', value: editorData.regiments }
        : null,
      editorData?.detachmentsSectionsUnits &&
      editorData?.detachmentsSectionsUnits?.length > 0 &&
      editorData?.detachmentsSectionsUnits?.[0]
        ? { title: isFrench ? 'DÉTACHEMENT/SECTION/UNITÉ' : 'Detachment/Section/Unit', value: editorData.detachmentsSectionsUnits }
        : null,
      editorData?.toursOfDuty &&
      editorData?.toursOfDuty?.length > 0 &&
      editorData?.toursOfDuty?.[0]
        ? { title: isFrench ? 'MISSIONS' : 'Tours of duty', value: editorData.toursOfDuty }
        : null,
      editorData?.postings &&
      editorData?.postings?.length > 0 &&
      editorData?.postings?.[0]
        ? { title: isFrench ? 'AFFECTATIONS' : 'Postings', value: editorData.postings }
        : null,
      editorData?.awardsAndHonors &&
      editorData?.awardsAndHonors?.length > 0 &&
      editorData?.awardsAndHonors?.[0]
        ? { title: isFrench ? 'HONNEURS ET DISTINCTIONS' : 'Awards and Honors', value: editorData.awardsAndHonors }
        : null,
    ];
  }, [editorData, editorData.lang]);

  const getValuesColumn1Exist = getValuesColumn1.some(x => x);
  const getValuesColumn2Exist = getValuesColumn2.some(x => x);
  const getValuesColumn3Exist = getValuesColumn3.some(x => x);
  const getValuesColumn4Exist = getValuesColumn4.some(x => x);
  

  const getItemValue = (value: string | string[] | ReactNode): string | ReactNode => {
    if (!value) return '';

    if (Array.isArray(value)) {
      return (
        <div className={styles.arrayType}>
          {value.map((item, index) => {
            return <span key={index}>{item}</span>;
          })}
        </div>
      );
    } else return value;
  };

  const getDateUnderPhoto = useMemo(() => {
    if (!editorData?.dateOfBirth || !editorData?.dateOfPassing) return;

    return `${editorData?.dateOfBirth.year} - ${editorData?.dateOfPassing.year}`;
  }, [editorData?.dateOfBirth, editorData?.dateOfPassing]);

  return (
    editorData && (
      <>
      {isVac() && (
        <Helmet>
          <title>{isFrench ? translationsHelmet?.titleFR : translationsHelmet?.titleEN}</title>
        </Helmet>
      )}
      <div className={`${isVac() ? 'isVac' : ''}`}>
        <div className={styles.content}>
          <div className={styles.contentLeft}>
            <div className={styles.contentLeftInside}>
              {editorData.previewImageUrl ? (
                <img
                  className={styles.details__addPhoto}
                  src={editorData.previewImageUrl}
                  alt={'preview'}
                />
              ) : editorData.finalImage?.full ? (
                <img
                  className={styles.details__addPhoto}
                  src={editorData.finalImage?.full}
                  alt={editorData.finalImage?.name}
                />
              ) : (
                <div className={styles.details__noImageText}>
                  <span>{t?.personPhotoUploader?.noImageLabel ?? 'No Image'}</span>
                </div>
              )}
              <span
                className={styles.title}
              >{`${editorData.firstName || ''} ${editorData?.lastName || ''}`}</span>
              <span className={styles.text}>{getDateUnderPhoto}</span>
              {editorData?.isDeceased && (
                <div className={styles.leafIcon}>
                  <img 
                  src={leafIcon} 
                  alt='Leaf Icon' 
                  style={{width: '24px'}}                
                  />
                  <small className={styles.leafIcontext}>{isFrench ? 'Indique que la personne vétérane est décédée' : 'Denotes Veteran is deceased'}</small>
                </div>
              )}
            </div>
            <div className={styles.leaves}></div>
          </div>

          <div className={styles.contentRight}>
            {(getValuesColumn3Exist || getValuesColumn4Exist) && (
              <div style={{display: 'flex', flexDirection: 'column'}}>
                <span className={styles.details__title}>{isFrench ? 'Détails sur le service' : 'Service Information'}</span>
                <div className={styles.contentRightColumns}>
                  <div className={styles.contentRightColumn1}>
                    {getValuesColumn3.map((item, index) => {
                      return item ? (
                        <div className={styles.contentItem} key={index}>
                          <span className={styles.title}>{item?.title}</span>
                          <span className={styles.text}>{getItemValue(item?.value)}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                  <div className={styles.contentRightColumn2}>
                    {getValuesColumn4.map((item, index) => {
                      return item ? (
                        <div className={styles.contentItem} key={index}>
                          <span className={styles.title}>{item?.title}</span>
                          <span className={styles.text}>{getItemValue(item?.value)}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>
            )}
            {(getValuesColumn1Exist || getValuesColumn2Exist) && (
              <div style={{display: 'flex', flexDirection: 'column'}}>
                <span className={styles.details__title}>{isFrench ? 'Renseignements personnels' : 'Personal Information'}</span>
                <div className={styles.contentRightColumns}>
                  <div className={styles.contentRightColumn1}>
                    {getValuesColumn1.map((item, index) => {
                      return item ? (
                        <div className={styles.contentItem} key={index}>
                          <span className={styles.title}>{item?.title}</span>
                          <span className={styles.text}>{getItemValue(item?.value)}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                  <div className={styles.contentRightColumn2}>
                    {getValuesColumn2.map((item, index) => {
                      return item ? (
                        <div className={styles.contentItem} key={index}>
                          <span className={styles.title}>{item?.title}</span>
                          <span className={styles.text}>{getItemValue(item?.value)}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </>
    )
  );
};
